import { createContext, useContext, useEffect, useState, useCallback, useMemo } from "react";
import api from "@/lib/api";
import { logDev } from "@/lib/ux";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = loading, false = unauth, obj = auth
  const [options, setOptions] = useState(null);

  // Session is established via the httpOnly cookie; probe /auth/me to hydrate.
  const loadMe = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data.data);
    } catch (e) {
      // 401 (no/expired session) is expected when logged out — fail gracefully.
      if (e.response?.status !== 401) logDev("Session check failed", e);
      setUser(false);
    }
  }, []);

  useEffect(() => {
    loadMe();
  }, [loadMe]);

  useEffect(() => {
    if (user) {
      api.get("/meta/options").then(({ data }) => setOptions(data.data)).catch((e) => logDev("Failed to load options", e));
    }
  }, [user]);

  // Re-fetch /meta/options on demand so changes made in Pipeline Settings
  // (stages/sub-stages: names, colours, order, active state) reflect across
  // the app (Kanban, filters, chips) without a full reload.
  const refreshOptions = useCallback(async () => {
    try {
      const { data } = await api.get("/meta/options");
      setOptions(data.data);
    } catch (e) { logDev("Failed to refresh options", e); }
  }, []);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    setUser(data.data.user); // cookie is set by the backend response
    return data.data.user;
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch (e) { logDev("Logout request failed (clearing locally)", e); }
    setUser(false);
  };

  const can = useCallback((action) => {
    if (!user) return false;
    const role = user.role;
    const map = {
      import: ["Admin"],
      manageUsers: ["Admin"],
      manageRules: ["Admin"],
      deleteLead: ["Admin"],
      reassign: ["Admin", "Sales Manager"],
      rerunAssignment: ["Admin", "Sales Manager"],
      createLead: ["Admin", "Sales Manager"],
      viewAllLeads: ["Admin", "Sales Manager"],
      viewDashboard: ["Admin", "Sales Manager", "Sales Executive"],
    };
    return (map[action] || []).includes(role);
  }, [user]);

  // v1.3 — permission-based access. Admin always has full access; otherwise check
  // the effective permission list returned by /auth/me.
  const has = useCallback((perm) => {
    if (!user) return false;
    if (user.role === "Admin") return true;
    return (user.permissions || []).includes(perm);
  }, [user]);

  const value = useMemo(
    () => ({ user, login, logout, can, has, options, refreshMe: loadMe, refreshOptions }),
    [user, can, has, options, loadMe, refreshOptions]
  );

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
